import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { createSx } from 'ui-lib/utils';
import {
  getErrorEntryLabel,
  getInlineErrorText,
  hasVisibleErrors,
  HttpErrorDetail,
  isErrorGroup,
  isIndexKey
} from '../../utils';

interface Props {
  errors: HttpErrorDetail;
  /** Message rendered by the snackbar itself, matching error entries are skipped to avoid duplicated data */
  message?: string;
  /** Label of the parent entry, used to name indexed collection items */
  label?: string;
  testID: string;
}

const sx = createSx({
  nested: {
    borderLeft: '1px solid',
    borderColor: 'neutral.outlineVariant',
    pl: 1
  },
  text: {
    wordBreak: 'break-word',
    color: 'neutral.onSurfaceVariant'
  }
});

const HttpErrorItem = ({ errors, message, label, testID }: Props) => {
  if (!hasVisibleErrors(errors, message)) return null;

  if (!isErrorGroup(errors)) {
    return (
      <Typography variant='body2' sx={sx.text} data-test-id={testID}>
        {String(errors)}
      </Typography>
    );
  }

  const entries = Object.entries(errors)
    .filter(([, error]) => hasVisibleErrors(error, message))
    .map(([key, error]) => ({
      key,
      error,
      entryTestID: `${testID}-${key}`,
      // plain messages of a collection are listed as is, only grouped ones need an item label
      entryLabel: isIndexKey(key) && !isErrorGroup(error) ? undefined : getErrorEntryLabel(key, label),
      inlineText: getInlineErrorText(error, message)
    }));

  return (
    <Box data-test-id={testID}>
      {entries.map(({ key, error, entryTestID, entryLabel, inlineText }) => {
        if (!entryLabel) {
          return (
            <HttpErrorItem
              key={key}
              errors={error}
              message={message}
              label={label}
              testID={entryTestID}
            />
          );
        }

        if (inlineText !== null) {
          return (
            <Stack key={key} direction='row' spacing={0.5} flexWrap='wrap' data-test-id={entryTestID}>
              <Typography variant='body2Bold' sx={sx.text} data-test-id={`${entryTestID}-label`}>
                {entryLabel}
              </Typography>
              <Typography variant='body2' sx={sx.text} data-test-id={`${entryTestID}-value`}>
                {inlineText}
              </Typography>
            </Stack>
          );
        }

        return (
          <Box key={key} data-test-id={entryTestID}>
            <Typography variant='body2Bold' sx={sx.text} data-test-id={`${entryTestID}-label`}>
              {entryLabel}
            </Typography>
            <Box sx={sx.nested}>
              <HttpErrorItem
                errors={error}
                message={message}
                label={entryLabel}
                testID={`${entryTestID}-value`}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default HttpErrorItem;
